import { motion } from "framer-motion";
import { FaArrowRight, FaCheck } from "react-icons/fa";
import { Link } from "react-router-dom";
import SectionEyebrow from "./SectionEyebrow";
import EngagementModels from "./EngagementModels";
import ClientReviews from "./ClientReviews";
import BrandButton from "./BrandButton";
import FAQ from "./FAQ";
import Industries from "./Industries";


const reveal = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const viewport = { once: false, amount: 0.18 };

const SectionIntro = ({ label, title, children }) => (
  <motion.div initial="hidden" whileInView="visible" viewport={viewport} variants={stagger} className="mx-auto mb-12 max-w-2xl text-center">
    <SectionEyebrow variants={reveal}>{label}</SectionEyebrow>
    <motion.h2 variants={reveal} className="mt-4 font-display text-3xl font-semibold leading-tight text-slate-900 sm:text-4xl lg:text-5xl">
      {title}
    </motion.h2>
    {children && <motion.p variants={reveal} className="mx-auto mt-4 max-w-xl text-base leading-7 text-slate-600">{children}</motion.p>}
  </motion.div>
);

const ServiceCard = ({ service, index }) => {
  const body = (
    <motion.article
      variants={reveal}
      whileHover={{
        y: -4,
        borderColor: service.accent,
        boxShadow: `0 16px 32px -16px ${service.accent}66`,
      }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      style={{ "--service-accent": service.accent }}
      className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white p-7 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.08)] transition-[border-color,box-shadow] duration-200"
    >
      {/* Thin accent bar on top */}
      <span className="absolute inset-x-0 top-0 h-[3px]" style={{ backgroundColor: service.accent }} />

      <div className="mb-8 flex items-start justify-between gap-4">
        <span
          className="inline-flex items-center rounded-md px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em]"
          style={{ backgroundColor: service.tint, color: service.accent }}
        >
          {String(index + 1).padStart(2, "0")}
        </span>
        <span
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border transition-transform duration-200 group-hover:scale-105"
          style={{
            backgroundColor: service.iconTint || service.tint,
            borderColor: `${service.iconColor || service.accent}40`,
            color: service.iconColor || service.accent,
          }}
        >
          {service.icon}
        </span>
      </div>

      <h3 className="font-display text-xl font-semibold text-slate-900 transition-colors duration-200 group-hover:text-[var(--service-accent)]">
        {service.title}
      </h3>
     <p className="mt-3 mb-6 text-sm leading-6 text-slate-600">{service.desc}</p>

      {/* Link is pushed to the bottom so all cards align */}
      <span
        className="mt-auto inline-flex items-center gap-2 border-t border-slate-100 pt-5 text-sm font-semibold"
        style={{ color: service.accent }}
      >
        Explore service <FaArrowRight className="h-3 w-3 transition-transform duration-200 group-hover:translate-x-1" />
      </span>
    </motion.article>
  );

  return service.path ? <Link to={service.path} className="block h-full">{body}</Link> : body;
};

export const LandingSections = ({ services, technologies, processSection, proofSection }) => (
  <>
    <section id="services" className="bg-slate-50 py-16 lg:py-20">
      <div className="mx-auto max-w-[84rem] px-4 sm:px-6 lg:px-8">
        <SectionIntro label="Our Areas of Expertise" title="Explore Our Digital Skillset">
          Engineering robust telecommunications, cognitive AI voice agents, and full-stack digital platforms built for business scale.
        </SectionIntro>
        <motion.div initial="hidden" whileInView="visible" viewport={viewport} variants={stagger} className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {services.map((service, index) => (
            <div key={service.title} className="h-full">
              <ServiceCard service={service} index={index} />
            </div>
          ))}
        </motion.div>
      </div>
    </section>

    <Industries />

    {processSection}

    <EngagementModels />

    {proofSection}

    <ClientReviews />

     <FAQ />

          <section id="contact" className="border-y border-slate-200/80 bg-slate-50 py-16 lg:py-24">
      <div className="mx-auto max-w-[84rem] px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={stagger}
          className="relative isolate overflow-hidden rounded-3xl border border-slate-200 bg-white px-6 py-14 text-center shadow-[0_1px_2px_rgba(15,23,42,0.04),0_24px_60px_-36px_rgba(76,29,149,0.3)] sm:px-12 lg:py-20"
        >
          {/* Hairline accent on top */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#8B4AFA]/60 to-transparent"
          />

          {/* Faint lavender wash at the top, no blobs */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-64 bg-gradient-to-b from-[#f5f0ff] to-transparent"
          />

          {/* Very faint grid, fading toward the edges */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 -z-10 [background-image:linear-gradient(to_right,rgba(139,74,250,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(139,74,250,0.05)_1px,transparent_1px)] [background-size:44px_44px] [mask-image:radial-gradient(ellipse_at_center,black_10%,transparent_65%)]"
          />

          <SectionEyebrow variants={reveal}>Get Consultation</SectionEyebrow>

          <motion.h2
            variants={reveal}
            style={{ lineHeight: 1.15 }}
            className="mx-auto mt-5 max-w-3xl font-display text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl"
          >
            Have a project in mind? Let&apos;s build something that{" "}
            <span className="bg-gradient-to-r from-[#7C3AED] to-[#C026D3] bg-clip-text text-transparent">
              matters.
            </span>
          </motion.h2>

          <motion.p
            variants={reveal}
            className="mt-5 text-base font-medium text-slate-800 sm:text-lg"
          >
            Empowering Businesses with Modern Technology
          </motion.p>

          <motion.p
            variants={reveal}
            className="mx-auto mt-3 max-w-2xl text-sm leading-7 text-slate-600 sm:text-base"
          >
            Our expert engineers combine creativity, performance, and innovation to deliver scalable
            solutions across modern technologies, ensuring your business thrives in the digital
            landscape.
          </motion.p>

          <motion.div
            variants={reveal}
            className="mt-8 flex flex-col items-center justify-center gap-5 sm:flex-row"
          >
            <BrandButton as={Link} to="/contact">
              Get Consultation
            </BrandButton>

            <BrandButton as={Link} to="/contact" variant="secondary">
              Hire Our Experts
            </BrandButton>
          </motion.div>
        </motion.div>
      </div>
    </section>
  </>
);
